import { Moon, Sun } from "@phosphor-icons/react";
import { useRef } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { type ThemeMode, useTheme } from "../providers/ThemeProvider";
import SimpleTooltip from "./SimpleTooltip";

const ICONS = { light: Sun, dark: Moon } as const;

export default function ThemeToggle() {
  const { mode, setMode } = useTheme();
  const { t } = useTranslation();
  const animating = useRef(false);

  const nextMode: ThemeMode = mode === "dark" ? "light" : "dark";
  const Icon = ICONS[mode === "system" ? "light" : mode];

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (animating.current) return;

    document.documentElement.style.setProperty("--ripple-x", `${e.clientX}px`);
    document.documentElement.style.setProperty("--ripple-y", `${e.clientY}px`);

    if (!("startViewTransition" in document)) {
      setMode(nextMode);
      return;
    }

    animating.current = true;

    const transition = (document as Document & {
      startViewTransition: (cb: () => void) => { finished: Promise<void> };
    }).startViewTransition(() => {
      flushSync(() => setMode(nextMode));
    });

    transition.finished.finally(() => {
      animating.current = false;
    });
  };

  const label = nextMode === "light"
    ? t("common.switchToLight", "Switch to Light")
    : t("common.switchToDark", "Switch to Dark");

  return (
    <SimpleTooltip content={label} placement="bottom">
      <button
        onClick={handleClick}
        className="flex items-center justify-center w-8 h-8 min-w-[32px] min-h-[32px] shrink-0 rounded-lg border border-gray-200 dark:border-neutral-700 bg-gray-100 dark:bg-neutral-800 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-150 mr-1"
      >
        <Icon size={15} weight="duotone" />
      </button>
    </SimpleTooltip>
  );
}
